import React, { useEffect, useMemo, useState, } from 'react';
import { midas } from '@resurs/midas';
import { classNames } from '@resurs/hephaestus';
const TextInput = ({ disabled = false, readonly = false, id, label, placeholder, type, size = 'normal', value = '', valid = false, required = false, format, disableAutocomplete = false, isValid, hint, skipValidation = false, autoFocus = false, onChange, onClick, onValidChange, additionalValidation, style, dataCy, children, }) => {
    const [isDirty, setIsDirty] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [isInternalValid, setInternalValid] = useState(!!valid);
    const [hasFormatError, setHasFormatError] = useState(false);
    const [hasRequiredError, setHasRequiredError] = useState(false);
    const [isFirst] = useState(true);
    const [isRequired, setIsRequired] = useState(!!required);
    const [internalValue, setInternalValue] = useState(value);
    const [shouldAdditionalValidationError, setShouldAdditionalValidationError] = useState(false);
    useEffect(() => {
        isValid === null || isValid === void 0 ? void 0 : isValid({ key: id, valid: isInternalValid });
    }, [isInternalValid]);
    const handleInvalid = (event) => {
        event.preventDefault();
        setIsRequired(true);
    };
    const handleChange = (event) => {
        setInternalValue(event.target.value);
        onChange === null || onChange === void 0 ? void 0 : onChange(event);
    };
    const test = (targetValue, dirty = true, nextValid = valid) => {
        const expr = format && format.regex ? format.regex : null;
        let localIsValid;
        let formatError = false;
        let requiredError = false;
        if (nextValid) {
            localIsValid = true;
        }
        else if (isRequired &&
            typeof targetValue === 'string' &&
            targetValue === '') {
            localIsValid = false;
            requiredError = true;
        }
        else if (!isRequired &&
            typeof targetValue === 'string' &&
            targetValue === '') {
            localIsValid = true;
            requiredError = false;
            formatError = false;
        }
        else if (targetValue && expr) {
            localIsValid = new RegExp(expr).test(`${targetValue}`);
            formatError = !localIsValid;
        }
        else
            localIsValid = !expr;
        const additionalValidationError = localIsValid &&
            !!(additionalValidation === null || additionalValidation === void 0 ? void 0 : additionalValidation(`${targetValue}`, localIsValid, id));
        localIsValid = localIsValid && !additionalValidationError;
        setIsDirty(dirty);
        setHasFormatError(formatError);
        setHasRequiredError(requiredError);
        setShouldAdditionalValidationError(additionalValidationError);
        setInternalValid(localIsValid);
        onValidChange === null || onValidChange === void 0 ? void 0 : onValidChange(id, localIsValid);
    };
    useEffect(() => {
        test(internalValue, false);
    }, []);
    useEffect(() => {
        setInternalValue(value);
        setIsRequired(!!required);
        test(value, false);
    }, [value, required]);
    const textInputProps = {
        id,
        name: id,
        placeholder,
        readOnly: readonly,
        disabled,
        required: !!required,
        type: type || 'text',
        value: value !== null && value !== void 0 ? value : '',
        pattern: format && format.regex ? format.regex : undefined,
        autoComplete: disableAutocomplete ? 'off' : undefined,
        autoFocus: !!autoFocus,
        onChange: (event) => {
            if (!disabled) {
                handleChange(event);
            }
        },
        onClick: (event) => {
            if (!disabled) {
                onClick === null || onClick === void 0 ? void 0 : onClick(event);
            }
        },
        onKeyUp: (event) => {
            if (!skipValidation) {
                test(event.currentTarget.value);
            }
        },
        onTouchEnd: (event) => {
            if (!skipValidation) {
                const eventValue = event.currentTarget.value; // store the value
                setTimeout(() => test(eventValue), 100);
            }
        },
        onFocus: () => {
            if (valid) {
                setInternalValid(true);
                setIsDirty(true);
            }
            setIsActive(true);
        },
        onBlur: (event) => {
            setIsActive(false);
            if (!skipValidation) {
                test(event.target.value);
            }
        },
        onInvalid: (event) => {
            if (!disabled) {
                handleInvalid(event);
            }
        },
    };
    const wrapperClassNames = useMemo(() => classNames(midas.formGroup, isDirty &&
        !isInternalValid &&
        isRequired &&
        hasRequiredError &&
        midas.hasWarning, isDirty && !isInternalValid && !isActive && midas.hasDanger, isDirty && !isInternalValid && !isFirst && isActive && midas.hasWarning, isDirty && isInternalValid && internalValue !== '' && midas.hasSuccess, isDirty && isInternalValid && internalValue !== '' && midas.hasSuccess, shouldAdditionalValidationError && midas.hasDanger), [
        isDirty,
        isInternalValid,
        isRequired,
        hasRequiredError,
        isActive,
        isFirst,
        internalValue,
        shouldAdditionalValidationError,
    ]);
    const textInputClassNames = useMemo(() => classNames(midas.formInput, size === 'small' && midas.inputSm, size === 'large' && midas.inputLg, size === 'xlarge' && midas.inputXl), [size]);
    const labelText = label ? (React.createElement("label", { className: midas.formLabel, htmlFor: id },
        label,
        " ",
        required && React.createElement("span", null, "*"))) : null;
    const hintValue = hint ? (React.createElement("span", { className: midas.formInputHint }, hint)) : null;
    const errorValue = isDirty &&
        !isInternalValid &&
        !isActive &&
        hasFormatError &&
        !hasRequiredError &&
        (format === null || format === void 0 ? void 0 : format.error) && React.createElement("span", { className: midas.formInputHint }, format.error);
    const additionalErrorValue = shouldAdditionalValidationError && (React.createElement("span", { className: midas.formInputHint }, shouldAdditionalValidationError));
    const requiredValue = isDirty &&
        !isActive &&
        isRequired &&
        hasRequiredError && React.createElement("span", { className: midas.formInputHint }, required);
    const inputGroup = (React.createElement("div", { className: midas.inputGroup },
        React.createElement("input", { className: textInputClassNames, ...textInputProps }),
        children));
    const input = useMemo(() => (React.createElement("input", { className: textInputClassNames, ...textInputProps, value: internalValue })), [textInputClassNames, textInputProps, internalValue]);
    return (React.createElement("div", { className: wrapperClassNames, "data-cy": dataCy, style: style },
        labelText,
        children ? inputGroup : input,
        hintValue,
        errorValue,
        requiredValue,
        additionalErrorValue));
};
export default TextInput;
